import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../../components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../components/ui/table";
import { Badge } from "../../components/ui/badge";
import { Button } from "../../components/ui/button";
import {
  Eye,
  Loader2,
  User,
  Phone,
  Mail,
  MapPin,
  Calendar,
  GraduationCap,
  School,
} from "lucide-react";
import { useState, useEffect, useCallback } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../../components/ui/dialog";
import { Label } from "../../components/ui/label";
import { toast } from "sonner";
import {
  studentAdmissionInquiryAPI,
  type StudentAdmissionInquiry,
} from "../../services/api";
import { formatDate } from "../../lib/utils";
import { CommonPagination } from "../../components/CommonPagination";

export default function StudentInquiries() {
  const [inquiries, setInquiries] = useState<StudentAdmissionInquiry[]>([]);
  const [loading, setLoading] = useState(true);
  const [totalCount, setTotalCount] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  const [selectedInquiry, setSelectedInquiry] =
    useState<StudentAdmissionInquiry | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);

  const fetchInquiries = useCallback(
    async (page = currentPage, size = pageSize) => {
      setLoading(true);
      try {
        const res = await studentAdmissionInquiryAPI.list({
          page,
          page_size: size,
        });
        // Based on user response format: { data: { inquiries: [], count: 0 } }
        const inquiriesList = res.data?.inquiries ?? [];
        const count = res.data?.count ?? inquiriesList.length ?? 0;

        setInquiries(inquiriesList);
        setTotalCount(count);
        setTotalPages(Math.ceil(count / size));
      } catch {
        toast.error("Failed to load student inquiries");
        setInquiries([]);
      } finally {
        setLoading(false);
      }
    },
    [currentPage, pageSize]
  );

  useEffect(() => {
    fetchInquiries(currentPage, pageSize);
  }, [currentPage, pageSize, fetchInquiries]);

  const handleViewInquiry = (inquiry: StudentAdmissionInquiry) => {
    setSelectedInquiry(inquiry);
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setSelectedInquiry(null);
  };

  return (
    <div className="space-y-6 p-6">
      <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight text-foreground">
            Student Admission Inquiries
          </h1>
          <p className="mt-1 text-muted-foreground">
            Manage and view all student interest submissions for admission
          </p>
        </div>
      </div>

      <Card className="shadow-premium overflow-hidden border-none bg-card/60 backdrop-blur-md">
        <CardHeader className="bg-muted/20 px-6 pb-3">
          <CardTitle className="flex items-center gap-3 text-xl font-bold">
            <GraduationCap className="h-6 w-6 text-primary" />
            Active Inquiries
            <Badge
              variant="secondary"
              className="ml-2 bg-primary/10 px-2.5 text-primary hover:bg-primary/20"
            >
              {totalCount} Total
            </Badge>
          </CardTitle>
        </CardHeader>
        <CardContent className="px-0 py-6 sm:px-6">
          <div className="overflow-hidden rounded-2xl border border-border/40 bg-background/50 shadow-sm">
            {loading ? (
              <div className="flex items-center justify-center py-32">
                <Loader2 className="h-10 w-10 animate-spin text-primary/60" />
              </div>
            ) : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader className="bg-muted/30">
                    <TableRow className="hover:bg-transparent">
                      <TableHead className="w-[80px] pl-6 text-xs font-bold uppercase tracking-wider text-muted-foreground">
                        ID
                      </TableHead>
                      <TableHead className="text-xs font-bold uppercase tracking-wider text-muted-foreground">
                        Student Profile
                      </TableHead>
                      <TableHead className="text-xs font-bold uppercase tracking-wider text-muted-foreground">
                        Contact & Verification
                      </TableHead>
                      <TableHead className="text-xs font-bold uppercase tracking-wider text-muted-foreground">
                        Academic Intent
                      </TableHead>
                      <TableHead className="text-xs font-bold uppercase tracking-wider text-muted-foreground">
                        Location
                      </TableHead>
                      <TableHead className="text-xs font-bold uppercase tracking-wider text-muted-foreground">
                        Submitted
                      </TableHead>
                      <TableHead className="pr-6 text-right text-xs font-bold uppercase tracking-wider text-muted-foreground">
                        Actions
                      </TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {inquiries.length === 0 ? (
                      <TableRow>
                        <TableCell
                          colSpan={7}
                          className="h-64 text-center text-muted-foreground"
                        >
                          <div className="flex flex-col items-center gap-3">
                            <div className="flex h-16 w-16 items-center justify-center rounded-full bg-muted">
                              <GraduationCap className="h-8 w-8 opacity-20" />
                            </div>
                            <p className="text-lg font-medium">
                              No admission inquiries found
                            </p>
                            <span className="text-sm opacity-60">
                              New inquiries will appear here as they are
                              submitted.
                            </span>
                          </div>
                        </TableCell>
                      </TableRow>
                    ) : (
                      inquiries.map((inquiry, index) => (
                        <TableRow
                          key={inquiry.id}
                          className="group border-b border-border/20 transition-all duration-200 last:border-0 hover:bg-muted/50"
                        >
                          <TableCell className="pl-6 font-mono text-xs text-muted-foreground">
                            {index + 1}
                          </TableCell>
                          <TableCell>
                            <div className="flex min-w-[200px] flex-col">
                              <span className="text-base font-semibold text-foreground transition-colors group-hover:text-primary">
                                {inquiry.full_name}
                              </span>
                              <span className="mt-0.5 flex items-center gap-1 text-xs italic text-muted-foreground">
                                <School className="h-3 w-3" />
                                {inquiry.college_school ?? "Private Inquiry"}
                              </span>
                            </div>
                          </TableCell>
                          <TableCell>
                            <div className="flex flex-col gap-1.5 py-2">
                              <div className="flex w-fit items-center gap-2.5 rounded-lg border border-border/30 bg-background/80 px-2 py-1 text-sm">
                                <Phone className="h-3.5 w-3.5 text-primary" />
                                <span className="font-medium tracking-tight">
                                  {inquiry.mobile}
                                </span>
                              </div>
                              <div className="flex items-center gap-2 pl-1 text-xs text-muted-foreground">
                                <Mail className="mr-1 h-3 w-3" />
                                <span className="max-w-[180px] truncate">
                                  {inquiry.email}
                                </span>
                              </div>
                            </div>
                          </TableCell>
                          <TableCell>
                            <div className="flex flex-col gap-1.5">
                              <span className="w-fit rounded-full border border-emerald-500/20 bg-emerald-500/10 px-2.5 py-1 text-xs font-bold text-emerald-600">
                                {inquiry.course_std_name ?? "General Inquiry"}
                              </span>
                              <div className="flex items-center gap-1.5 pl-1">
                                <div className="h-1.5 w-1.5 rounded-full bg-primary" />
                                <span className="text-xs font-medium text-muted-foreground">
                                  Score:{" "}
                                  <span className="text-foreground">
                                    {inquiry.last_result_percentage}%
                                  </span>
                                </span>
                              </div>
                            </div>
                          </TableCell>
                          <TableCell>
                            <div className="flex max-w-[200px] flex-col gap-1">
                              <div className="flex items-start gap-1.5 text-sm">
                                <MapPin className="mt-0.5 h-3.5 w-3.5 flex-shrink-0 text-rose-500" />
                                <span className="line-clamp-2 leading-tight">
                                  {inquiry.address}
                                </span>
                              </div>
                              <span className="pl-5 text-[10px] font-bold uppercase text-muted-foreground">
                                {inquiry.pincode} •{" "}
                                {inquiry.preferred_state_name ?? "GJ"}
                              </span>
                            </div>
                          </TableCell>
                          <TableCell>
                            <div className="flex flex-col gap-1">
                              <div className="flex items-center gap-1.5 text-[11px] font-medium uppercase text-muted-foreground">
                                <Calendar className="h-3 w-3" />
                                {formatDate(inquiry.created_at)}
                              </div>
                              {inquiry.auth_status ? (
                                <Badge className="h-4 bg-emerald-500 py-0 text-[10px]">
                                  Verified
                                </Badge>
                              ) : (
                                <Badge
                                  variant="outline"
                                  className="h-4 py-0 text-[10px] opacity-50"
                                >
                                  Pending
                                </Badge>
                              )}
                            </div>
                          </TableCell>
                          <TableCell className="pr-6 text-right">
                            <Button
                              size="sm"
                              variant="outline"
                              className="group/btn h-8 gap-1.5 hover:border-primary hover:bg-primary"
                              onClick={() => handleViewInquiry(inquiry)}
                            >
                              <Eye className="h-3.5 w-3.5 transition-transform group-hover/btn:scale-110" />
                              View Detail
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </div>
            )}

            {!loading && inquiries.length > 0 && (
              <div className="border-t border-border/40 bg-muted/10 p-4">
                <CommonPagination
                  currentPage={currentPage}
                  totalPages={totalPages}
                  pageSize={pageSize}
                  totalCount={totalCount}
                  onPageChange={setCurrentPage}
                  onPageSizeChange={size => {
                    setPageSize(size);
                    setCurrentPage(1);
                  }}
                />
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Inquiry Detail Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-h-[90vh] max-w-4xl overflow-hidden rounded-2xl border-none p-0 shadow-2xl">
          <div className="relative bg-gradient-to-r from-primary/95 to-primary p-6 text-primary-foreground">
            <div className="pointer-events-none absolute right-0 top-0 p-8 opacity-10">
              <GraduationCap size={120} />
            </div>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-5 text-3xl font-extrabold tracking-tight drop-shadow-sm">
                <div className="flex h-16 w-16 items-center justify-center rounded-2xl border border-white/30 bg-white/20 shadow-inner backdrop-blur-md">
                  <User size={32} className="text-white" />
                </div>
                <div>
                  <span className="block leading-none">
                    {selectedInquiry?.full_name}
                  </span>
                  <span className="mt-2 block text-[12px] font-bold uppercase tracking-[0.2em] opacity-80">
                    Student Admission Inquiry • ID: {selectedInquiry?.id}
                  </span>
                </div>
              </DialogTitle>
            </DialogHeader>
          </div>

          <div className="max-h-[calc(90vh-140px)] space-y-10 overflow-y-auto bg-background p-8">
            {selectedInquiry && (
              <div className="space-y-12">
                {/* Section 1: Contact Detail Cards */}
                <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
                  <div className="group flex flex-col items-center rounded-2xl border border-blue-100 bg-blue-50/50 p-6 text-center transition-all hover:shadow-md dark:border-blue-900/30 dark:bg-blue-950/20">
                    <div className="mb-3 flex h-12 w-12 items-center justify-center rounded-2xl bg-blue-100 transition-transform group-hover:scale-110 dark:bg-blue-900/50">
                      <Phone className="h-6 w-6 text-blue-600 dark:text-blue-400" />
                    </div>
                    <span className="mb-1 text-[11px] font-bold uppercase tracking-[0.15em] text-blue-600/70 dark:text-blue-400/70">
                      Phone Number
                    </span>
                    <p className="text-base font-semibold tracking-tight text-blue-950 dark:text-blue-50">
                      {selectedInquiry.mobile}
                    </p>
                  </div>
                  <div className="group flex flex-col items-center rounded-2xl border border-emerald-100 bg-emerald-50/50 p-6 text-center transition-all hover:shadow-md dark:border-emerald-900/30 dark:bg-emerald-950/20">
                    <div className="mb-3 flex h-12 w-12 items-center justify-center rounded-2xl bg-emerald-100 transition-transform group-hover:scale-110 dark:bg-emerald-900/50">
                      <Mail className="h-6 w-6 text-emerald-600 dark:text-emerald-400" />
                    </div>
                    <span className="mb-1 text-[11px] font-bold uppercase tracking-[0.15em] text-emerald-600/70 dark:text-emerald-400/70">
                      Email Inquiry
                    </span>
                    <p className="w-full truncate text-base font-semibold tracking-tight text-emerald-950 dark:text-emerald-50">
                      {selectedInquiry.email}
                    </p>
                  </div>
                  <div className="group flex flex-col items-center rounded-2xl border border-amber-100 bg-amber-50/50 p-6 text-center transition-all hover:shadow-md dark:border-amber-900/30 dark:bg-amber-950/20">
                    <div className="mb-3 flex h-12 w-12 items-center justify-center rounded-2xl bg-amber-100 transition-transform group-hover:scale-110 dark:bg-amber-900/50">
                      <MapPin className="h-6 w-6 text-amber-600 dark:text-amber-400" />
                    </div>
                    <span className="mb-1 text-[11px] font-bold uppercase tracking-[0.15em] text-amber-600/70 dark:text-amber-400/70">
                      Location Code
                    </span>
                    <p className="text-base font-semibold tracking-tight text-amber-950 dark:text-amber-50">
                      {selectedInquiry.pincode} •{" "}
                      {selectedInquiry.preferred_state_name ?? "Gujarat"}
                    </p>
                  </div>
                </div>

                {/* Section 2: Address & Profile */}
                <section className="relative overflow-hidden rounded-3xl border border-border/50 bg-muted/30 p-8 shadow-inner">
                  <div className="absolute right-0 top-0 p-4 opacity-5">
                    <MapPin size={100} />
                  </div>
                  <div className="grid grid-cols-1 gap-12 md:grid-cols-2">
                    <div className="space-y-4">
                      <h4 className="flex items-center gap-2 text-sm font-black uppercase tracking-[0.1em] text-primary/80">
                        <MapPin className="h-4 w-4" /> Permanent Address
                      </h4>
                      <div className="min-h-[100px] rounded-2xl border border-border/40 bg-background p-5 shadow-sm">
                        <p className="text-lg font-medium italic leading-relaxed">
                          {selectedInquiry.address}
                        </p>
                        <div className="mt-4 flex flex-wrap gap-2">
                          <Badge
                            variant="secondary"
                            className="border border-primary/10 bg-primary/5 font-bold text-primary"
                          >
                            {selectedInquiry.preferred_zone_name ?? "N/A"} Zone
                          </Badge>
                          <Badge
                            variant="secondary"
                            className="border border-primary/10 bg-primary/5 font-bold text-primary"
                          >
                            {selectedInquiry.preferred_state_name}
                          </Badge>
                        </div>
                      </div>
                    </div>
                    <div className="space-y-4">
                      <h4 className="flex items-center gap-2 text-sm font-black uppercase tracking-[0.1em] text-primary/80">
                        <User className="h-4 w-4" /> Submission Context
                      </h4>
                      <div className="space-y-4 rounded-2xl border border-border/40 bg-background p-5 shadow-sm">
                        <div>
                          <Label className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground">
                            Submitted On
                          </Label>
                          <p className="mt-1 flex items-center gap-2 font-bold">
                            <Calendar className="h-4 w-4 text-primary/60" />
                            {formatDate(selectedInquiry.created_at)}
                          </p>
                        </div>
                        <div>
                          <Label className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground">
                            Origin User
                          </Label>
                          <p className="mt-1 flex items-center gap-2 font-bold">
                            <Mail className="h-4 w-4 text-primary/60" />
                            {selectedInquiry.submitted_by_email ??
                              "Direct Public Submission"}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </section>

                {/* Section 3: Academic Highlights */}
                <section className="space-y-6">
                  <h3 className="rounded-full bg-primary/5 py-2 text-center text-sm font-black uppercase tracking-[0.1em] text-primary/60">
                    Academic Profile & Selection Intent
                  </h3>
                  <div className="grid grid-cols-1 gap-4 md:grid-cols-4">
                    <div className="group relative overflow-hidden rounded-3xl bg-primary p-6 text-white shadow-lg transition-all hover:bg-primary/90 md:col-span-2">
                      <School className="absolute -bottom-2 right-2 h-20 w-20 text-white/10 transition-transform group-hover:scale-125" />
                      <Label className="text-[11px] font-bold uppercase tracking-widest text-white/60">
                        College / School Name
                      </Label>
                      <p className="mt-1 text-xl font-black leading-tight">
                        {selectedInquiry.college_school ??
                          "Private Application"}
                      </p>
                      <div className="mt-3 flex items-center gap-2">
                        <Badge className="border-none bg-white/20 font-bold hover:bg-white/30">
                          {selectedInquiry.course_std_name ?? "Standard"}
                        </Badge>
                      </div>
                    </div>
                    <div className="flex flex-col items-center justify-center rounded-3xl border border-slate-800 bg-slate-900 p-6 text-white shadow-lg">
                      <Label className="mb-1 text-[11px] font-bold uppercase tracking-widest text-slate-500">
                        Academic Result
                      </Label>
                      <div className="text-3xl font-black text-emerald-400 drop-shadow-[0_0_15px_rgba(52,211,153,0.3)]">
                        {selectedInquiry.last_result_percentage}%
                      </div>
                    </div>
                    <div className="flex flex-col items-center justify-center rounded-3xl border border-border/50 bg-white p-6 shadow-lg">
                      <Label className="mb-1 text-[11px] font-bold uppercase tracking-widest text-muted-foreground">
                        Inquiry Status
                      </Label>
                      <Badge
                        className={
                          selectedInquiry.auth_status
                            ? "bg-emerald-500 px-4 py-1 font-bold"
                            : "bg-muted px-4 py-1 font-bold text-muted-foreground"
                        }
                      >
                        {selectedInquiry.auth_status ? "VERIFIED" : "PENDING"}
                      </Badge>
                    </div>
                  </div>
                </section>

                {/* Section 4: Remarks & Location Prefs */}
                <section className="grid grid-cols-1 gap-8 md:grid-cols-2">
                  <div className="space-y-4">
                    <h4 className="flex items-center gap-2 pl-1 text-xs font-black uppercase tracking-widest text-muted-foreground">
                      Preferred Zones & Areas
                    </h4>
                    <div className="space-y-5 rounded-3xl border bg-muted p-5">
                      <div>
                        <Label className="mb-2 block text-[10px] font-bold uppercase tracking-[0.15em] text-muted-foreground">
                          Cities Preference
                        </Label>
                        <div className="flex flex-wrap gap-2">
                          {selectedInquiry.preferred_cities_names?.map(c => (
                            <Badge
                              key={c}
                              variant="secondary"
                              className="border-none bg-background font-bold text-foreground shadow-sm"
                            >
                              {c}
                            </Badge>
                          )) ?? (
                            <span className="text-xs italic text-muted-foreground">
                              No preference selected
                            </span>
                          )}
                        </div>
                      </div>
                      <div>
                        <Label className="mb-2 block text-[10px] font-bold uppercase tracking-[0.15em] text-muted-foreground">
                          Specific Areas
                        </Label>
                        <div className="flex flex-wrap gap-1.5">
                          {selectedInquiry.preferred_areas_names?.map(a => (
                            <Badge
                              key={a}
                              variant="outline"
                              className="border-primary/10 bg-background/50 text-[10px]"
                            >
                              {a}
                            </Badge>
                          )) ?? (
                            <span className="text-xs italic text-muted-foreground">
                              No specific areas
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="space-y-4">
                    <h4 className="flex items-center gap-2 pl-1 text-xs font-black uppercase tracking-widest text-muted-foreground">
                      Submission Remarks
                    </h4>
                    <div className="space-y-4 rounded-3xl border bg-muted p-5">
                      <div>
                        <Label className="mb-1 block text-[10px] font-bold uppercase tracking-[0.15em] text-muted-foreground">
                          Last Year Note
                        </Label>
                        <p className="rounded-xl border border-border/40 bg-background/50 p-3 text-sm italic">
                          &quot;
                          {selectedInquiry.last_year_remark ??
                            "No additional notes provided."}
                          &quot;
                        </p>
                      </div>
                      <div>
                        <Label className="mb-1 block text-[10px] font-bold uppercase tracking-[0.15em] text-muted-foreground">
                          Hostel/Internal Remark
                        </Label>
                        <div className="rounded-xl border border-primary/10 bg-primary/5 p-3 text-sm font-medium">
                          {selectedInquiry.hostel_remark ??
                            "No specific requests for hostel allocation."}
                        </div>
                      </div>
                    </div>
                  </div>
                </section>

                <div className="flex justify-center pt-4">
                  <Button
                    onClick={handleCloseDialog}
                    className="h-12 rounded-full px-12 font-black uppercase tracking-widest shadow-xl transition-all hover:scale-105 active:scale-95"
                  >
                    Close Student Portfolio
                  </Button>
                </div>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
